import {PredictService} from '../../../services/predict.service';
import {distanceVisibleInCamera, getCameraBoundingBox, subdivideBbox, toBBoxString} from '../cesiumTools';
import * as turf from '@turf/turf';
import { LayerDatasource, LayerLoadType, LayerType, MapperViewer} from '../types';

export class ZmiLayer implements LayerDatasource {
  id = 'zmi';
  name = 'ZMI';
  active = true;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  type = LayerType.DataSource;
  loadType = LayerLoadType.Manual;
  viewer: MapperViewer;

  /* Custom properties */
  zmiColor = Cesium.Color.fromCssColorString('#80ffff').withAlpha(0.6);
  zmiColorMoyen = Cesium.Color.fromCssColorString('#4472c3').withAlpha(0.6);
  zmiColorFort = Cesium.Color.fromCssColorString('#002060').withAlpha(0.6);
  detailVisible = true;
  previouslyLoadedZmi = null;
  lavetorMap = new Map();
  isLoading = false;

  hasDetail = true;

  constructor(
    private predictService: PredictService,
  ) {}

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  setDetailVisible(visible: boolean): void {
    this.detailVisible = visible;
    this.applyStyle();
  }

  loadData(forceReload = false): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    const bbox = toBBoxString(getCameraBoundingBox(this.viewer));

    if (this.previouslyLoadedZmi === bbox && !forceReload) {
      return;
    }

    this.previouslyLoadedZmi = bbox;
    this.isLoading = true;

    this.fetchZmi(bbox).then((features) => {
      this.buildZmi(this.dedupeFeatures(features));
      this.isLoading = false;
    });
  }

  private fetchZmi(bbox: string): Promise<any[]> {
    return new Promise((resolve) => {
      this.predictService.getZmi(bbox).subscribe((geojson) => {
        resolve(geojson.features);
      }, () => {
        const [west, north, east, south] = bbox.split(',').map(Number);
        const subBboxes = subdivideBbox([west, south, east, north]);

        if (subBboxes.length === 0) {
          resolve([]);

          return;
        }

        Promise.all(
          subBboxes.map(([subWest, subSouth, subEast, subNorth]) =>
            this.fetchZmi(`${subWest},${subNorth},${subEast},${subSouth}`))
        ).then((results) => resolve(results.flat()));
      });
    });
  }

  private dedupeFeatures(features: any[]): any[] {
    const featureById = new Map<string, any>();

    features.forEach((feature) => {
      const id = feature.properties.id;
      const existing = featureById.get(id);

      if (!existing || turf.area(feature) > turf.area(existing)) {
        featureById.set(id, feature);
      }
    });

    return Array.from(featureById.values());
  }

  buildZmi(features): void {
    features.map((item) => {
      if (item.properties.type_alea === 'LAVE TORRENTIELLE') {
        this.lavetorMap.set(item.properties.id, item);
      }
    });

    const zmi = features.filter((item) => item.properties.type_alea !== 'LAVE TORRENTIELLE');

    let laveTorFeatures = [];
    const _laveTorPolygons = [];

    if (this.lavetorMap.size > 0) {
      this.lavetorMap.forEach((item) => {
        _laveTorPolygons.push(...item.geometry.coordinates);
      });


      const union = _laveTorPolygons.reduce((acc, item) => {
        let unionturf = turf.union(acc, turf.polygon([item]));
        if (!unionturf) {
          unionturf = turf.polygon([item]);
        }
        return unionturf ;
      }, turf.polygon([_laveTorPolygons[0]]));

      if (union.geometry.type === 'Polygon') {
        laveTorFeatures.push(union);
      } else {
        laveTorFeatures.push(...union.geometry.coordinates.map((item) => turf.polygon(item)));
      }

      laveTorFeatures = laveTorFeatures.map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          type_alea: 'LAVE TORRENTIELLE',
          surface: turf.area(item)
        }
      }));
    }


    try{
      this.dataSource.load({
        type: 'FeatureCollection',
        features: [...zmi, ...laveTorFeatures]
      }, {
        stroke: this.zmiColor,
        fill: this.zmiColor,
        strokeWidth: 3,
        clampToGround: true,
      }).then(() => {
        this.applyStyle();
        this.setActive(true);
      });
    } catch (error){
      console.log(error);
    }
  }

  applyStyle(): void {
    this.dataSource.entities.values.forEach((item) => {
      if (item.properties.type_alea.getValue() === 'LAVE TORRENTIELLE') {
        item.polygon.material = this.createStripeMaterial(item);
        item.polygon.stRotation = Cesium.Math.toRadians(45) as any;
        return;
      } else if (item.properties.type_alea.getValue() === 'LAVE TORRENTIELLE BORDER') {
        // item.polyline.stroke = Cesium.Color.AQUA.withAlpha(1);
        return;
      }

      if (this.detailVisible) {
        switch (item.properties.niveau.getValue()) {
          case 'FORT':
            item.polygon.material = this.zmiColorFort;
            break;
          case 'MOYEN':
            item.polygon.material = this.zmiColorMoyen;
            break;
          default:
            item.polygon.material = this.zmiColor;
            break;
        }
      } else {
        item.polygon.material = this.zmiColor;
      }
    });
  }

  public computeStripeRepeat(surface: number): number {
    let repeat = 0;
    if (surface > 1000000) {
      repeat = surface / 4200;
    } else if (surface > 100000) {
      repeat = surface / 1200;
    } else if (surface > 10000) {
      repeat = surface / 100;
    } else if (surface > 1000) {
      repeat = surface / 10;
    } else {
      repeat = surface;
    }

    return repeat;

  }


  createStripeMaterial(entity: any): any {
    return new Cesium.StripeMaterialProperty({
      evenColor: this.zmiColor,
      oddColor: Cesium.Color.TRANSPARENT,
      repeat: this.computeStripeRepeat(entity.properties.surface),
      offset: 0
    });
  }

  setActive(active: boolean): void  {
    this.active = active;
    this.dataSource.show = active;
  }

  onAfterPrint(): void {
  }

  onBeforePrint(): void {
  }
}
